import React, { useMemo, useState, useEffect } from 'react';
import { Table, Button, Space, message, Input, DatePicker, Switch, Tag, Radio } from 'antd';
import { PlusOutlined, SearchOutlined, AuditOutlined, ReloadOutlined } from '@ant-design/icons';
import { ActionButtons } from './utils';
import type { ColumnsType } from 'antd/es/table';
import { ModalForm, ProFormText, ProFormDateTimePicker } from '@ant-design/pro-components';
import { Form } from 'antd';
import ReactQuill from 'react-quill';
import dayjs from 'dayjs';
import { normalizeQuillHtml } from '../../utils/richText';
import { quillClipboardForceParagraphMatchers } from '../../utils/quillClipboardFix';

interface Information {
  id: number;
  information_title: string;
  information_content: string;
  article_type: string;
  tag: string;
  policy_level: string;
  is_top: number;
  release_time: string;
  create_time: string;
}

export default function InfoPolicy() {
  const [data, setData] = useState<Information[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Information | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [form] = Form.useForm();
  const [content, setContent] = useState('');
  const [isTop, setIsTop] = useState(false);

  // 富文本编辑器（仅文字编辑工具栏：去掉图片/附件/公式等）
  const quillModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ size: ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['clean'],
      ],
      clipboard: {
        matchVisual: false,
        matchers: quillClipboardForceParagraphMatchers,
      },
    }),
    [],
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/information?type=政策法规', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setData(resp.data || []);
      } else {
        message.error(resp?.error || '获取政策法规列表失败');
      }
    } catch (err) {
      message.error('获取政策法规列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldValue('release_time', dayjs());
    setContent('');
    setIsTop(false);
    setModalVisible(true);
  };

  const handleEdit = (record: Information) => {
    setEditingItem(record);
    form.setFieldsValue({
      information_title: record.information_title,
      tag: record.tag,
      policy_level: record.policy_level,
      release_time: record.release_time ? dayjs(record.release_time) : undefined,
    });
    setContent(normalizeQuillHtml(record.information_content));
    setIsTop(record.is_top === 1);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/information/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('删除成功');
        fetchData();
      } else {
        message.error(resp?.error || '删除失败');
      }
    } catch (err) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!content.trim() || content === '<p><br></p>') {
        message.error('请填写信息内容');
        return;
      }
      
      const token = localStorage.getItem('admin_token');
      const url = editingItem 
        ? `/api/admin/information/${editingItem.id}`
        : '/api/admin/information';
      const method = editingItem ? 'PUT' : 'POST';

      const submitData = {
        information_title: values.information_title,
        information_content: content,
        article_type: '政策法规',
        tag: values.tag || null,
        policy_level: values.policy_level,
        is_top: isTop ? 1 : 0,
        release_time: values.release_time?.format?.('YYYY-MM-DD HH:mm:ss'),
      };

      const resp = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(submitData),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success(editingItem ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchData();
      } else {
        message.error(resp?.error || '操作失败');
      }
    } catch (err: any) {
      if (err.errorFields) {
        message.error('请填写所有必填项');
      } else {
        message.error('操作失败');
      }
    }
  };

  const handleUpdateReleaseTime = async (record: Information, newTime: dayjs.Dayjs | null) => {
    if (!newTime) return;
    
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch(`/api/admin/information/${record.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          release_time: newTime.format('YYYY-MM-DD HH:mm:ss'),
        }),
      }).then(r => r.json());

      if (resp && resp.ok) {
        message.success('时间更新成功');
        fetchData();
      } else {
        message.error(resp?.error || '更新失败');
      }
    } catch (err) {
      message.error('更新失败');
    }
  };

  // 过滤数据
  const filteredData = data.filter(a => 
    a.information_title?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const columns: ColumnsType<Information> = [
    {
      title: '标题',
      dataIndex: 'information_title',
      key: 'information_title',
      width: 180,
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '信息内容',
      dataIndex: 'information_content',
      key: 'information_content',
      width: 200,
      render: (html: string) => {
        const text = html?.replace(/<[^>]+>/g, '') || '';
        const displayText = text.length > 30 ? text.slice(0, 30) + '...' : text;
        return <span style={{ color: '#6b7280' }} title={text}>{displayText}</span>;
      }
    },
    {
      title: '分级',
      dataIndex: 'policy_level',
      key: 'policy_level',
      width: 100,
      render: (level: string) => {
        if (!level) return '-';
        let color = 'blue';
        if (level === '国家级') color = 'red';
        else if (level === '省级') color = 'blue';
        else if (level === '市县级') color = 'green';
        return <Tag color={color}>{level}</Tag>;
      }
    },
    {
      title: '标签',
      dataIndex: 'tag',
      key: 'tag',
      width: 100,
      render: (tag: string) => tag ? <Tag color="blue">{tag}</Tag> : '-'
    },
    {
      title: '置顶',
      dataIndex: 'is_top',
      key: 'is_top',
      width: 80,
      render: (isTop: number) => isTop === 1 ? <Tag color="red">置顶</Tag> : '-'
    },
    {
      title: '发布时间',
      dataIndex: 'release_time',
      key: 'release_time',
      width: 200,
      render: (text, record) => (
        <DatePicker
          showTime
          value={text ? dayjs(text) : undefined}
          onChange={(date) => handleUpdateReleaseTime(record, date)}
          format="YYYY-MM-DD HH:mm:ss"
          style={{ width: '100%' }}
        />
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_, record) => (
        <ActionButtons
          onEdit={() => handleEdit(record)}
          onDelete={() => handleDelete(record.id)}
          deleteTitle="确定要删除这条信息吗？"
        />
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <AuditOutlined />
          </div>
          <div>
            <h2>政策法规</h2>
            <p>管理政策法规信息</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索标题..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchData}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            style={{ 
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
            }}
          >
            新增文章
          </Button>
        </Space>
      </div>
      
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredData.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 'max-content' }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
      
      <ModalForm
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <AuditOutlined style={{ color: 'white', fontSize: 16 }} />
            </div>
            <span>{editingItem ? '编辑文章' : '新增文章'}</span>
          </div>
        }
        open={modalVisible}
        onOpenChange={setModalVisible}
        form={form}
        onFinish={handleSubmit}
        width={1000}
        modalProps={{
          destroyOnHidden: true,
          centered: true,
          wrapClassName: 'admin-richtext-modal',
        }}
        submitter={{
          searchConfig: { submitText: '确定', resetText: '取消' },
        }}
      >
        <div style={{ marginTop: 24 }}>
          <ProFormText
            name="information_title"
            label="标题"
            placeholder="请输入标题"
            rules={[{ required: true, message: '请输入标题' }]}
          />
          
          <Form.Item label="信息内容" required>
            <ReactQuill
              className="admin-richtext-editor"
              theme="snow"
              value={content || ''}
              onChange={setContent}
              modules={quillModules}
              placeholder="请输入文章内容..."
              style={{ height: 350, marginBottom: 60 }}
            />
          </Form.Item>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <ProFormDateTimePicker
              name="release_time"
              label="发布时间"
              placeholder="请选择发布时间"
              rules={[{ required: true, message: '请选择发布时间' }]}
            />
            
            <Form.Item
              name="policy_level"
              label="分级"
              rules={[{ required: true, message: '请选择分级' }]}
            >
              <Radio.Group buttonStyle="solid">
                <Radio.Button value="国家级">国家级</Radio.Button>
                <Radio.Button value="省级">省级</Radio.Button>
                <Radio.Button value="市县级">市县级</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </div>
          
          <ProFormText
            name="tag"
            label="标签"
            placeholder="请输入标签，如：政策解读、法规动态、通知公告等"
          />
          
          <Form.Item label="置顶">
            <Switch 
              checked={isTop} 
              onChange={setIsTop}
              checkedChildren="是"
              unCheckedChildren="否"
            />
            <span style={{ marginLeft: 8, color: '#6b7280', fontSize: 13 }}>
              置顶文章将在列表中优先显示
            </span>
          </Form.Item>
        </div>
      </ModalForm>
    </div>
  );
}
